import eventClientApi from "@/api-client/event-client";
import { cleanObject } from "@/lib/helper";
import { getQueryKey } from "@key.ai/constants";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";

interface ApiResponsePagination<T> {
  data: T[];
  count?: number;
  pageParam?: number;
  totalRecords?: number;
}

interface UseGuestlistParams {
  groupId: string | undefined;
  search?: string;
}

export const useGuestlist = ({ groupId, search }: UseGuestlistParams) => {
  const queryKey = getQueryKey("EVENT_GROUP_PARTICIPANTS");

  const fetchGuestlist = async ({ pageParam = 1 }: { pageParam?: unknown }) => {
    const { data } = await eventClientApi
      .get(`api/v1/groups/${groupId}/guests`, {
        searchParams: cleanObject({
          page: pageParam as number,
          limit: 10,
          search: search
        })
      })
      .json<any>();

    return {
      ...data,
      totalRecords: data?.count,
      pageParam
    };
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    status,
    refetch,
    isFetching
  } = useInfiniteQuery<ApiResponsePagination<any>>({
    queryKey: [queryKey, groupId, search],
    queryFn: fetchGuestlist,
    getNextPageParam: (lastPage) => {
      if (lastPage?.data?.length >= 10) return lastPage.pageParam! + 1;
      return undefined;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    enabled: !!groupId,
    gcTime: 0,
    refetchOnMount: true,
    refetchInterval: false
  });

  const totalRecords = data?.pages?.[0]?.totalRecords ?? 0;

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    status,
    refetch,
    totalRecords,
    isFetching
  };
};
